import React, { useEffect, useRef, useState } from 'react';
import useCOS from '@/pages/dashboard/hooks/useCos';
import { useIntl, useSelector } from 'umi';
import { noop } from '@/pages/home/utils/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';

import './index.scss';
import { Icon, Input } from 'tea-component';
import { v4 as uuidv4 } from 'uuid';

interface PdfUploaderProps {
  onFileUploaded?: () => void;
  onSuccess?: (path: string) => void;
  onError?: (error: Error) => void;
  onDelete?: () => void;

  pdfFile: File | null;
  setPdfFile: (file: File | null) => void;

  pdfFileUploadStatus: string;
  setPdfFileUploadStatus: (status: string) => void;

  value?: string;
  onChange?: (val: any) => void;
  onBlur?: any;
}

const PdfUploader: React.FC<PdfUploaderProps> = ({
  onFileUploaded,
  onSuccess,
  onError,
  onDelete,

  pdfFile,
  setPdfFile,

  pdfFileUploadStatus,
  setPdfFileUploadStatus,

  value,
  onChange,
  onBlur,
}) => {
  const { formatMessage } = useIntl();
  const { putObject = noop, deleteObject = noop } = useCOS();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileInputPath, setFileInputPath] = useState<string>("");
  const [cloudPath, setCloudPath] = useState<string>("");

  const [fileRemoved, setFileRemoved] = useState<boolean>(false);

  useEffect(() => {
    if (!fileRemoved && (cloudPath !== "")) {
      onSuccess && onSuccess(cloudPath);
      setPdfFileUploadStatus('success');
    }
  }, [cloudPath]);

  const handleFileChange = async () => {
    setFileRemoved(false);

    if (!fileInputRef.current || !fileInputRef.current.files) {
      return;
    }

    const file = fileInputRef.current.files[0];
    if (!file) {
      return;
    }

    // Check if the file has a PDF extension
    const allowedExtensions = ["pdf"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      onError && onError(
        new Error('menu_pdf_format_err')
      );
      return;
    }

    onFileUploaded && onFileUploaded();
    setPdfFileUploadStatus('uploading');

    setPdfFile(file);

    try {
      const path = `dashboard_PDF/${uuidv4()}.pdf`;

      setFileInputPath(`/${path}`);

      const result = await putObject({ path, file });

      setCloudPath("https://" + result.Location);
      
    } catch (error) {
      console.log('Upload failed:', error);
      setPdfFileUploadStatus('error');
      onError && onError(
        new Error('menu_pdf_uploaded_err')
      );
    } finally {
    }
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = async () => {
    onDelete && onDelete();
    setPdfFile(null);
    setFileRemoved(true);
    setPdfFileUploadStatus('idle');
    // setFileInputPath("");
    try {
      await deleteObject({ path: fileInputPath });
    } catch (exception) {
      console.warn(exception);
    }
  };

  return (
    <div className='file-input-group'>
      <div className='upload-file-container'>
        {pdfFile !== null &&
          <button className='close-file-btn' onClick={() => handleRemoveFile()}>
            <Icon type="dismiss" color='#9B9FB0' size='l' />
          </button>
        }
        <Input
          className='file-input'
          value={pdfFile !== null ? pdfFile.name : value}
          onChange={onChange}
          disabled={pdfFile !== null ? true : false}
          style={{ paddingRight: pdfFile !== null ? 36 : 0 }}
          onBlur={onBlur}
          placeholder={formatMessage({ id: 'menu_path_pdf_placeholder' })}
        />
      </div>

      <Input
        type="file"
        accept=".pdf"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />
      <button className="upload-file-btn" onClick={handleClick}>
        {(pdfFileUploadStatus === "uploading") ?
          <Icon type="loading" />
          :
          <SvgIcon type="upload" />
        }

        {/* <SvgIcon type="upload" /> */}
      </button>
    </div>
  );
};

export default PdfUploader;
